import React from "react";
import { motion } from "framer-motion";
import { ClipboardCheck, Sparkles, ShieldCheck } from "lucide-react";

export default function Hero() {
  return (
    <section id="home" className="pt-16 relative overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-10 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] bg-[#EAF0E2] px-4 py-2 rounded-full mb-6">
              Independent food safety support
            </span>

            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#16231A] tracking-tight leading-[1.1] mb-6">
              Your Partner{" "}
              <span className="text-[#7CA63A]">in Food Safety</span>
            </h1>

            <p className="text-base md:text-lg leading-relaxed text-[#2B4131] mb-8 max-w-lg">
              Professional audits, hygiene inspections, compliance support and FSSAI license assistance for restaurants, cafes, cloud kitchens, bakeries and food manufacturing units.
            </p>

            <div className="flex flex-wrap gap-3 mb-10">
              <a
                href="#contact"
                className="bg-[#7CA63A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#68902A] transition-all duration-300 hover:-translate-y-0.5"
                data-testid="hero-cta-btn"
              >
                Get Started
              </a>
              <a
                href="#services"
                className="bg-[#EAF0E2] text-[#2B4131] px-8 py-4 rounded-full font-medium hover:bg-[#DCE6CF] transition-all duration-300"
                data-testid="hero-services-btn"
              >
                Explore Services
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: ClipboardCheck, title: "Food Safety Audits", desc: "Detailed on-site reviews with clear action points" },
                { icon: Sparkles, title: "Hygiene Checks", desc: "Practical inspections covering sanitation standards" },
                { icon: ShieldCheck, title: "FSSAI Support", desc: "Licensing assistance for eligible food businesses" },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                  className="bg-white border border-[#E3E8DC] rounded-xl p-4 shadow-[0_4px_20px_rgb(0,0,0,0.03)]"
                >
                  <item.icon size={18} className="text-[#7CA63A] mb-2" />
                  <h3 className="font-['Outfit'] text-sm font-semibold text-[#16231A] mb-1">{item.title}</h3>
                  <p className="text-xs text-[#526B57] leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="hidden lg:block"
          >
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-[0_20px_60px_rgb(0,0,0,0.1)]">
                <img
                  src="https://images.unsplash.com/photo-1763219805214-91fa634e6006?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwa2l0Y2hlbiUyMGNoZWYlMjBjbGVhbnxlbnwwfHx8fDE3NzU4OTQ0ODR8MA&ixlib=rb-4.1.0&q=85"
                  alt="Chef preparing food in a clean commercial kitchen"
                  className="w-full h-[520px] object-cover"
                  data-testid="hero-image"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 border border-[#E3E8DC] shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#EAF0E2] rounded-full flex items-center justify-center">
                    <ShieldCheck size={18} className="text-[#7CA63A]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#526B57]">Trusted by</p>
                    <p className="font-['Outfit'] text-sm font-bold text-[#16231A]">50+ Food Businesses</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
